import { useEffect, useState } from 'react';
import Icon from './Icon.jsx';
import Button from './Button.jsx';
import styles from './InstallBanner.module.scss';

const DISMISS_KEY = 'recall.installDismissedAt';
const DISMISS_MS = 14 * 24 * 3600 * 1000;

export function isIOS() {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
  );
}

export function isSafari() {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|Android/.test(ua);
}

let deferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event('recall:installable'));
  });
}

export function useInstallState() {
  const [canPrompt, setCanPrompt] = useState(() => !!deferredPrompt);
  useEffect(() => {
    const on = () => setCanPrompt(true);
    window.addEventListener('recall:installable', on);
    return () => window.removeEventListener('recall:installable', on);
  }, []);
  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanPrompt(false);
    return outcome === 'accepted';
  };
  return { canPrompt, install, standalone: isStandalone(), ios: isIOS() };
}

/**
 * Safari can evict website storage after 7 days without a visit; installed
 * Home Screen apps are exempt. So we actively nudge iOS/Safari users to install.
 */
export default function InstallBanner() {
  const { canPrompt, install, standalone, ios } = useInstallState();
  const [dismissed, setDismissed] = useState(() => {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - at < DISMISS_MS;
  });
  const safariLike = ios || isSafari();
  if (standalone || dismissed || (!canPrompt && !safariLike)) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div className={styles.banner} role="region" aria-label="Install app">
      <div className={styles.top}>
        <span className={styles.icon}>
          <Icon name="shield" size={22} />
        </span>
        <div className={styles.text}>
          <strong>Add Recall to your Home Screen</strong>
          {canPrompt ? (
            <span>It opens instantly, works offline, and keeps your cards safe on this device.</span>
          ) : (
            <span>
              Tap <Icon name="share" size={15} /> <b>Share</b>, then <b>Add to Home Screen</b>. Safari can
              clear data for websites you haven’t opened in 7 days; installed apps keep it.
            </span>
          )}
        </div>
      </div>
      <div className={styles.actions}>
        <Button size="sm" onClick={dismiss}>
          Not now
        </Button>
        {canPrompt ? (
          <Button size="sm" variant="primary" onClick={install}>
            Install
          </Button>
        ) : null}
      </div>
    </div>
  );
}
